import { createFileRoute, lazyRouteComponent, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

// Dynamically import LandingPage so Vite ships its CSS in a separate chunk
// loaded only when the landing route activates. Without this indirection,
// the CSS imported from the landing page would land in the main bundle
// because routes/index.tsx is statically imported by routeTree.gen.ts.
const LazyLandingPage = lazyRouteComponent(() => import("@/components/landing/LandingPage"));

export const Route = createFileRoute("/")({
  component: IndexPage,
});

interface AutomatonSummary {
  id: string;
  name: string;
  directive: string;
  status: "active" | "dormant" | "dead";
  paused_by_user: boolean;
  energy_balance: number;
  heartbeat_interval_seconds: number;
  spawned_at: string;
}

interface ListResponse {
  automatons: AutomatonSummary[];
}

const CADENCE_OPTIONS = [
  { value: 3600, label: "1h" },
  { value: 14400, label: "4h" },
  { value: 21600, label: "6h" },
  { value: 43200, label: "12h" },
  { value: 86400, label: "24h" },
];

function statusColor(status: string) {
  if (status === "active") return "var(--color-energy)";
  if (status === "dead") return "var(--color-death)";
  return "var(--color-signal)";
}

function AutomatonCard({ a }: { a: AutomatonSummary }) {
  const queryClient = useQueryClient();

  const patchMutation = useMutation({
    mutationFn: async (body: { heartbeat_interval_seconds?: number; paused_by_user?: boolean }) => {
      const res = await api.patch<{ automaton: AutomatonSummary }>(`/v1/automatons/${a.id}`, body);
      return res;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["automatons"] });
    },
  });

  return (
    <div className="dash-card" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}
      >
        <Link
          to="/a/$automatonName"
          params={{ automatonName: a.name }}
          style={{
            fontFamily: "var(--font-pixel)",
            fontSize: 20,
            color: "var(--color-fg-1)",
            textDecoration: "none",
          }}
        >
          {a.name.toUpperCase()}
        </Link>
        <span
          style={{
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: statusColor(a.status),
            padding: "2px 8px",
            border: `1px solid ${statusColor(a.status)}`,
            borderRadius: 2,
          }}
        >
          {a.status}
        </span>
      </div>

      <div style={{ display: "flex", gap: 20, fontSize: 13, color: "var(--color-fg-2)" }}>
        <span>
          <span style={{ color: "var(--color-fg-3)", fontSize: 11 }}>WALLET </span>
          <span style={{ color: "var(--color-energy)", fontFamily: "var(--font-pixel)" }}>
            {a.energy_balance}c
          </span>
        </span>
        <span>
          <span style={{ color: "var(--color-fg-3)", fontSize: 11 }}>CADENCE </span>
          {a.heartbeat_interval_seconds / 3600}h
        </span>
      </div>

      {a.status !== "dead" && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className={`dash-action-btn ${a.paused_by_user ? "dash-action-btn--feed" : "dash-action-btn--work"}`}
            style={{ fontSize: 11, padding: "4px 10px" }}
            disabled={patchMutation.isPending}
            onClick={() => patchMutation.mutate({ paused_by_user: !a.paused_by_user })}
          >
            {a.paused_by_user ? "Resume" : "Pause"}
          </button>

          <select
            aria-label={`Cadence for ${a.name}`}
            style={{
              background: "var(--color-canvas-4)",
              color: "var(--color-fg-2)",
              border: "1px solid var(--color-canvas-line-2)",
              borderRadius: 2,
              padding: "4px 8px",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              cursor: "pointer",
            }}
            value={a.heartbeat_interval_seconds}
            onChange={(e) =>
              patchMutation.mutate({ heartbeat_interval_seconds: parseInt(e.target.value) })
            }
            disabled={patchMutation.isPending}
          >
            {CADENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery<ListResponse>({
    queryKey: ["automatons"],
    queryFn: async () => {
      const res = await api.get<ListResponse>("/v1/automatons");
      if (!res.ok) throw new Error("Failed to load automatons");
      return res.body!;
    },
  });

  return (
    <div className="dash-page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <h2
          style={{
            fontFamily: "var(--font-pixel)",
            fontSize: 24,
            color: "var(--color-fg-1)",
            margin: 0,
          }}
        >
          MY AUTOMATONS
        </h2>
        <button
          type="button"
          className="pg-btn pg-btn--accent"
          onClick={() => void navigate({ to: "/spawn" })}
        >
          + Spawn new
        </button>
      </div>

      {isLoading && (
        <p style={{ color: "var(--color-fg-3)", fontFamily: "var(--font-pixel)" }}>Loading…</p>
      )}
      {isError && <p style={{ color: "var(--color-death)" }}>Failed to load your automatons.</p>}
      {data && data.automatons.length === 0 && (
        <div
          style={{
            padding: "40px 24px",
            textAlign: "center",
            color: "var(--color-fg-3)",
            border: "1px dashed var(--color-canvas-line-2)",
            borderRadius: 4,
          }}
        >
          <p style={{ fontFamily: "var(--font-pixel)", fontSize: 18, marginBottom: 16 }}>
            No automatons yet.
          </p>
          <button type="button" className="pg-btn" onClick={() => void navigate({ to: "/spawn" })}>
            Spawn your first one →
          </button>
        </div>
      )}
      {data && data.automatons.length > 0 && (
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          }}
        >
          {data.automatons.map((a) => (
            <AutomatonCard key={a.id} a={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function IndexPage() {
  const { user, loading } = useAuth();

  if (loading) return null;
  if (user) return <Dashboard />;
  return <LazyLandingPage />;
}
